/* GASSISA
=========================================================================================
* Descripción: Buscador de traslados por máquina (pendientes PE con folio +
*              aplicados TR del kardex). Solo datos de la PC indicada.
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 23/09/2026 | LIC. José Luis Aragón C.                       | Creación
*      2       | 08/10/2026 | LIC. José Luis Aragón C.                       | Botón Imprimir TR: abre reporte
*              |            |                                  | periodo SQL del día+origen/destino
*              |            |                                  | en pestaña nueva (ver TR terminado
*              |            |                                  | sin cargarlo al formulario)
* ---------------------------------------------------------------------------------------
*/

import TextField from "../ui/TextField";
import { useEffect, useState } from "react";
import { Modal, Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Typography } from "@mui/material";

import { useRequest } from "../../context/System/RequestContext";
import { useSesion } from "../../context/System/SesionContext";
import { apiTrasladosBuscar, apiReporteTrasladosPeriodo } from "../../settings/ApiConfig";
import SizeSoftXLoadingProcess from "../leyout/SizeSoftXLoadingProcess";

export default function ModalBuscarTraslados({ openModal, handleCloseModal, pc, onSelectFolio }) {
  const { GetRequest, PostFileRequest } = useRequest();
  const { parametrosCajaForm, usuario, tipoUsuario } = useSesion();
  const [filtro, setFiltro] = useState("");
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [imprimiendoFolio, setImprimiendoFolio] = useState(0);

  // Rev 2: ver TR terminado en pantalla sin cargarlo al formulario.
  // El GET imprimirtraslado/{pc} es del PE en curso, no sirve para un TR
  // histórico; se usa el reporte periodo SQL (misma fuente que Historial
  // del día) acotado a fecha+origen+destino del renglón.
  const handleImprimirTR = async (r) => {
    const folioRef = Number(r?.folio) || 0;
    setImprimiendoFolio(folioRef);
    try {
      const fechaDia = r?.fecha ? String(r.fecha).slice(0, 10) : "";
      const hoy = new Date();
      const yyyy = hoy.getFullYear();
      const mm = String(hoy.getMonth() + 1).padStart(2, "0");
      const dd = String(hoy.getDate()).padStart(2, "0");
      const body = {
        fechainicio: fechaDia || `${yyyy}-${mm}-${dd}`,
        fechafin: fechaDia || `${yyyy}-${mm}-${dd}`,
        almacenorigen: String(r?.origen || "TODOS").trim().toUpperCase() || "TODOS",
        almacendestino: String(r?.destino || "TODOS").trim().toUpperCase() || "TODOS",
        filtrodescripcion: "",
        sucursal: parametrosCajaForm?.sucursal ?? "",
        pc: (parametrosCajaForm?.caja || pc || "").trim(),
        usuario: usuario || "",
        tipousuario: tipoUsuario || "USUARIO",
      };
      await PostFileRequest({ url: apiReporteTrasladosPeriodo, body, openInNewTab: true, loader: `Generando traslado folio ${folioRef}...` });
    } catch {} finally {
      setImprimiendoFolio(0);
    }
  };

  const buscar = async (texto) => {
    if (!pc) return;
    setCargando(true);
    try {
      const q = String(texto || "").trim() || "-";
      const url = apiTrasladosBuscar
        .replace("{pc}", encodeURIComponent(pc.trim()))
        .replace("{busqueda}", encodeURIComponent(q));
      const response = await GetRequest({ url });
      if (!response?.hasError && !response?.HasError && Array.isArray(response?.result)) {
        setLista(response.result);
      } else {
        setLista([]);
      }
    } catch {
      setLista([]);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (openModal) {
      setFiltro("");
      setLista([]);
      buscar("");
    }
  }, [openModal, pc]);

  return (
    <Modal open={!!openModal} onClose={handleCloseModal}>
      <Box sx={{ width: "82%", maxHeight: "84vh", margin: "6vh auto", background: "white", padding: 2, borderRadius: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Traslados de la méquina {pc || ""}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField autoComplete="off"
            fullWidth
            size="small"
            name="Filtro"
            label="Buscar por folio, clave o descripción"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                buscar(filtro);
              }
            }}
          />
          <Button variant="contained" size="small" onClick={() => buscar(filtro)}>
            Buscar
          </Button>
        </Box>
        <TableContainer sx={{ overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {["Folio", "Fecha", "Origen", "Destino", "Partidas", "Total", "Status", ""].map((t) => (
                  <TableCell key={t} sx={{ bgcolor: "primary.main", color: "primary.contrastText", fontWeight: 700 }}>
                    {t}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {lista.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    Sin traslados para la méquina
                  </TableCell>
                </TableRow>
              ) : lista.map((r, idx) => (
                <TableRow key={`${r.folio}-${r.status}-${idx}`} hover>
                  <TableCell sx={{ fontWeight: 700 }}>{r.folio}</TableCell>
                  <TableCell>{r.fecha ? String(r.fecha).slice(0, 10) : ""}</TableCell>
                  <TableCell>{r.origen}</TableCell>
                  <TableCell>{r.destino}</TableCell>
                  <TableCell align="center">{r.partidas}</TableCell>
                  <TableCell align="center">{r.total}</TableCell>
                  <TableCell>
                    <Chip
                      label={r.status}
                      size="small"
                      color={r.status === "TR" ? "default" : "warning"}
                      sx={{ fontWeight: 700 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {r.status === "PE" ? (
                      <Button size="small" variant="outlined" onClick={() => onSelectFolio && onSelectFolio(r.folio)}>
                        Cargar
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        disabled={imprimiendoFolio === Number(r?.folio)}
                        onClick={() => handleImprimirTR(r)}
                        title={`Ver traslado folio ${r.folio} terminado`}
                      >
                        Imprimir
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant="outlined" size="small" onClick={handleCloseModal}>
            Cerrar
          </Button>
        </Box>
        <SizeSoftXLoadingProcess open={cargando} message="Buscando traslados..." />
      </Box>
    </Modal>
  );
}
